<script setup lang="ts">
import type { Surface, SurfaceCollection } from '~/types/api'

const props = defineProps<{
  productLineNid?: number
  collection: SurfaceCollection
  categoryTitle?: string
  isEven?: boolean
}>()

const surfaces = ref<Surface[]>([])
const loading = ref(false)

const store = useSurfacesCollectionsStore()
function getCollection(collectionId: number) {
  store.getSurfaceCollections(collectionId).then((res) => {
    surfaces.value = res
    loading.value = false
  })
}

const surfacePropertiesKeys = [
  'composition',
  'fabricWeight',
  'fabricWidth',
  'frictionResistance',
  'colorResistanceLight',
  'colorResistanceFriction',
  'pilling',
  'fireResistance',
] as const

type ParametersKeys = Extract<
  keyof typeof props.collection,
  (typeof surfacePropertiesKeys)[number]
>

const surfaceParameters = computed(() => {
  const params = {} as Record<ParametersKeys, string>
  for (const key of surfacePropertiesKeys) {
    const value = props.collection[key]
    if (value && value !== '') {
      params[key] = value
    }
  }
  return params
})

const selectedSurface = ref<Surface | null>(null)

const showAvailableProducts = ref(false)

watch(
  () => props.collection,
  () => {
    loading.value = true
    getCollection(props.collection.nid)
    selectedSurface.value = null
  },
  {
    immediate: true,
  }
)

// TODO: is this modal in use?
function toggleAvailableProducts() {
  showAvailableProducts.value = !showAvailableProducts.value
}
</script>

<template>
  <div>
    <div
      v-if="categoryTitle"
      class="cursor-pointer pb-5 text-xl font-bold"
      @click="toggleAvailableProducts"
    >
      {{ collection.title + ' - ' + categoryTitle }}
    </div>
    <UiModal
      v-model="showAvailableProducts"
      title-path="base.products.surfaces.availableProducts"
      title-class="text-xl"
    >
      <ProductLinesProductsForSurfaceCollection
        :product-line-nid="productLineNid"
        :surface-collection-nid="collection.nid"
      />
    </UiModal>
    <div class="grid grid-cols-1 border-b-4 border-neutral-100 lg:grid-cols-5">
      <div
        class="col-span-5 border-neutral-100 py-8 lg:col-span-2 lg:border-r-2 lg:py-10 lg:pr-10"
      >
        <div class="relative flex h-full flex-col gap-y-2">
          <h2 class="font-pragmatica-ext mb-2 text-xl font-bold">
            {{ collection.title }}
          </h2>
          <div
            v-for="(value, key) in surfaceParameters"
            :key="key"
            class="flex flex-col gap-x-10 sm:flex-row sm:justify-between lg:flex-col xl:flex-row"
          >
            <div class="font-pragmatica-ext whitespace-nowrap font-bold">
              {{ $t('base.products.surfaces.parameters.' + key) }}
            </div>
            <UTooltip
              class="truncate"
              :text="value"
              :popper="{ placement: 'top' }"
              :ui="{
                wrapper: 'relative inline max-w-fit',
                color: 'text-white',
                background: 'bg-black',
                rounded: 'rounded-none',
                base: '[@media(pointer:coarse)]:hidden h-6 mr-[7px] px-3 py-3 text-sm font-normal break-normal whitespace-normal h-fit after:absolute after:left-1/2 after:left-[100%] after:-rotate-90 after:-translate-x-1/2 after:border-8 after:border-x-transparent after:border-b-transparent after:border-t-black',
              }"
            >
              <p class="truncate">
                {{ value }}
              </p>
            </UTooltip>
          </div>
          <div
            v-if="selectedSurface"
            class="absolute bottom-0 left-0 right-0 top-0"
          >
            <NuxtImg
              v-if="selectedSurface.image"
              class="h-full w-full object-cover"
              sizes="576px"
              loading="lazy"
              :src="withHost(selectedSurface.image?.url)"
              :alt="selectedSurface.image?.metaAlt || undefined"
              :title="selectedSurface.image?.metaTitle || undefined"
              @click="selectedSurface = null"
            />
          </div>
        </div>
      </div>
      <div
        class="col-span-5 border-neutral-100 pb-8 lg:col-span-3 lg:border-l-2 lg:py-10 lg:pl-10"
      >
        <div v-if="loading" class="w-full">
          <UiSkeletonSurfacesGrid
            class="min-[528px]:grid-cols-4 sm:grid-cols-5 md:grid-cols-5 2xl:grid-cols-7"
          />
        </div>
        <!-- TODO: could be ProductSurfacesGrid component -->
        <div
          v-else
          class="grid w-full grid-cols-3 gap-4 min-[528px]:grid-cols-4 sm:grid-cols-5 md:grid-cols-5 2xl:grid-cols-7"
        >
          <div v-for="surface in surfaces" :key="surface.nid" class="group">
            <NuxtImg
              class="mb-2 aspect-square w-full cursor-pointer"
              :class="{ 'border-8 border-black': selectedSurface === surface }"
              sizes="150px"
              loading="lazy"
              :src="withHost(surface.image?.url)"
              :alt="surface.image?.metaAlt || undefined"
              :title="surface.image?.metaTitle || undefined"
              @click="
                selectedSurface === surface
                  ? (selectedSurface = null)
                  : (selectedSurface = surface)
              "
            />
            <UTooltip
              :text="surface.title"
              :popper="{ placement: 'top' }"
              :ui="{
                wrapper: 'relative inline',
                color: 'text-white',
                background: 'bg-black',
                rounded: 'rounded-none',
                base: '[@media(pointer:coarse)]:hidden h-6 px-3 py-3 text-sm font-normal break-normal whitespace-normal h-fit after:absolute after:left-1/2 after:top-[100%] after:-translate-x-1/2 after:border-8 after:border-x-transparent after:border-b-transparent after:border-t-black',
              }"
            >
              <div
                class="font-pragmatica-ext truncate text-center text-sm font-bold"
              >
                {{ surface.title }}
              </div>
            </UTooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
